import { useCallback, useEffect, useMemo } from 'react';
import {
  differenceInMonths,
  differenceInWeeks,
  endOfMonth,
  endOfWeek,
  isAfter,
  isBefore,
  startOfMonth,
  startOfWeek,
  subDays,
  subMonths,
  subWeeks,
} from 'date-fns';
import { useIntl } from 'react-intl';
import { useAppStore } from 'hooks/state/useAppStore';
import { DateRangePicker } from 'components/filter/input/DateRangePicker/DateRangePicker';
import type {
  CalendarGranularity,
  DateValue,
  GranularityToggleOption,
  PresetOption,
} from 'components/filter/input/DateRangePicker/types';
import { WEEKLY_START_DAY_OPTIONS } from 'components/filter/input/DateRangePicker/types';
import {
  WEEKLY_PRESET_REGISTRY,
  WEEKLY_COMPARISON_REGISTRY,
  MONTHLY_PRESET_REGISTRY,
  MONTHLY_COMPARISON_REGISTRY,
  buildPresetOptions,
  buildComparisonOptions,
  recalcComparison,
} from 'components/filter/input/DateRangePicker/presets';
import { isValidDateRange } from 'components/filter/input/DateRangePicker/validation';
import { isValidComparisonRange } from 'components/filter/input/DateRangePicker/validation';
import {
  BENCHMARKING_WEEKLY_PRESET_IDS,
  BENCHMARKING_WEEKLY_COMPARISON_PRESET_IDS,
  BENCHMARKING_MONTHLY_PRESET_IDS,
  BENCHMARKING_MONTHLY_COMPARISON_PRESET_IDS,
} from './presets';
import {
  BENCHMARKING_TEMPORAL_TYPE_WEEKLY,
  BENCHMARKING_TEMPORAL_TYPE_MONTHLY,
  type BenchmarkingTemporalType,
} from './types';

export interface BenchmarkingDateRangePickerProps {
  caption: string;
  disabled?: boolean;
}

/** Granularity options for the toggle */
const GRANULARITY_OPTIONS: GranularityToggleOption[] = [
  { value: 'weekly', labelId: 'datepicker.granularity.weekly' },
  { value: 'monthly', labelId: 'datepicker.granularity.monthly' },
];

/** Map CalendarGranularity to BenchmarkingTemporalType */
function toTemporalType(granularity: CalendarGranularity): BenchmarkingTemporalType {
  return granularity === 'weekly' ? BENCHMARKING_TEMPORAL_TYPE_WEEKLY : BENCHMARKING_TEMPORAL_TYPE_MONTHLY;
}

/** Map BenchmarkingTemporalType to CalendarGranularity */
function toGranularity(temporalType: BenchmarkingTemporalType): CalendarGranularity {
  return temporalType === BENCHMARKING_TEMPORAL_TYPE_WEEKLY ? 'weekly' : 'monthly';
}

/** Get the maximum allowed end date for monthly mode (end of last month) */
function getMonthlyMaxDate(): Date {
  return endOfMonth(subMonths(new Date(), 1));
}

/**
 * Calculates min/max date bounds for benchmarking pickers.
 */
function useDateBounds(isWeekly: boolean, mainRange: { from?: Date; to?: Date }) {
  return useMemo(() => {
    if (isWeekly) {
      const diffWeeks = mainRange?.from && mainRange?.to ? differenceInWeeks(mainRange.to, mainRange.from) : 0;
      const max = endOfWeek(subWeeks(startOfWeek(new Date(), WEEKLY_START_DAY_OPTIONS), 1), WEEKLY_START_DAY_OPTIONS);
      return {
        mainMinDate: startOfWeek(subWeeks(max, 44), WEEKLY_START_DAY_OPTIONS),
        maxDate: max,
        comparisonMinDate: startOfWeek(subWeeks(max, 96), WEEKLY_START_DAY_OPTIONS),
        comparisonMaxDate: endOfWeek(
          startOfWeek(subWeeks(max, diffWeeks), WEEKLY_START_DAY_OPTIONS),
          WEEKLY_START_DAY_OPTIONS
        ),
      };
    }
    // Monthly
    const max = subDays(startOfMonth(new Date()), 1);
    return {
      mainMinDate: startOfMonth(subMonths(max, 11)),
      maxDate: max,
      comparisonMinDate: startOfMonth(subMonths(max, 23)),
      comparisonMaxDate: max,
    };
  }, [isWeekly, mainRange?.from, mainRange?.to]);
}

/**
 * Main date range picker for Benchmarking module.
 * Connected to the app store for global benchmarking date range state.
 * Includes integrated granularity toggle (weekly/monthly).
 * Automatically recalculates comparison when main range changes.
 */
// eslint-disable-next-line react-refresh/only-export-components
function Main({ caption, disabled = false }: BenchmarkingDateRangePickerProps) {
  const intl = useIntl();
  const today = useMemo(() => new Date(), []);
  const { benchmarkingDateRange, setBenchmarkingDateRange } = useAppStore();

  const isWeekly = benchmarkingDateRange.temporalType === BENCHMARKING_TEMPORAL_TYPE_WEEKLY;
  const granularity = toGranularity(benchmarkingDateRange.temporalType);
  const { mainMinDate, maxDate } = useDateBounds(isWeekly, benchmarkingDateRange.range);

  // Build preset options based on current granularity
  const presetOptions = useMemo<PresetOption[]>(() => {
    if (isWeekly) {
      return buildPresetOptions(BENCHMARKING_WEEKLY_PRESET_IDS, WEEKLY_PRESET_REGISTRY, today);
    }
    return buildPresetOptions(BENCHMARKING_MONTHLY_PRESET_IDS, MONTHLY_PRESET_REGISTRY, today);
  }, [isWeekly, today]);

  // Compute the value to pass to the picker (strip comparison, picker doesn't need it)
  const fieldValue: DateValue = useMemo(
    () => ({ ...benchmarkingDateRange, comparison: undefined }),
    [benchmarkingDateRange]
  );

  // Disable dates outside the allowed range
  const disabledDates = useCallback(
    (date: Date) => {
      if (mainMinDate && date < mainMinDate) return true;
      if (maxDate && date > maxDate) return true;
      return false;
    },
    [mainMinDate, maxDate]
  );

  // Validate and fix date range on mount/granularity change for monthly mode
  // This handles cases where persisted state has invalid dates (e.g., current month selected,
  // or a stale custom range whose fromDate has fallen outside the 11-month backend window)
  useEffect(() => {
    if (isWeekly) return;

    const monthlyMaxDate = getMonthlyMaxDate();
    const rangeFromDate = benchmarkingDateRange.range.from;
    const rangeToDate = benchmarkingDateRange.range.to;

    const toDateInvalid = rangeToDate && isAfter(rangeToDate, monthlyMaxDate);
    const fromDateInvalid = rangeFromDate && isBefore(rangeFromDate, mainMinDate);

    if (toDateInvalid || fromDateInvalid) {
      const monthlyPresets = buildPresetOptions(BENCHMARKING_MONTHLY_PRESET_IDS, MONTHLY_PRESET_REGISTRY, new Date());
      const defaultPreset = monthlyPresets.find(p => p.value !== 'custom') ?? monthlyPresets[0];

      setBenchmarkingDateRange({
        ...benchmarkingDateRange,
        range: {
          from: defaultPreset?.from,
          to: defaultPreset?.to,
          presetValue: defaultPreset?.value,
          presetLabel: defaultPreset?.label,
        },
      });
    }
  }, [isWeekly, benchmarkingDateRange, setBenchmarkingDateRange, mainMinDate]);

  // Handle granularity change
  const handleGranularityChange = useCallback(
    (newGranularity: CalendarGranularity) => {
      const newTemporalType = toTemporalType(newGranularity);
      if (newTemporalType !== benchmarkingDateRange.temporalType) {
        // Reset to default range for the new granularity
        const newToday = new Date();
        const newIsWeekly = newGranularity === 'weekly';
        const newPresets = newIsWeekly
          ? buildPresetOptions(BENCHMARKING_WEEKLY_PRESET_IDS, WEEKLY_PRESET_REGISTRY, newToday)
          : buildPresetOptions(BENCHMARKING_MONTHLY_PRESET_IDS, MONTHLY_PRESET_REGISTRY, newToday);

        // Select the first non-custom preset as default
        const defaultPreset = newPresets.find(p => p.value !== 'custom') ?? newPresets[0];
        const newRange = {
          from: defaultPreset?.from,
          to: defaultPreset?.to,
          presetValue: defaultPreset?.value,
          presetLabel: defaultPreset?.label,
        };

        // Compute a default comparison preset for the new granularity
        const newComparison = newIsWeekly
          ? recalcComparison(
              { range: newRange, comparison: { presetValue: 'previous_period' } },
              { range: newRange },
              WEEKLY_COMPARISON_REGISTRY,
              'previous_period'
            )
          : recalcComparison(
              { range: newRange, comparison: { presetValue: 'previous_period' } },
              { range: newRange },
              MONTHLY_COMPARISON_REGISTRY,
              'previous_period'
            );
        const validComparison = isValidComparisonRange(newComparison ?? {})
          ? newComparison
          : { presetValue: 'no_comparison' };

        setBenchmarkingDateRange({
          temporalType: newTemporalType,
          range: newRange,
          comparison: validComparison,
        });
      }
    },
    [benchmarkingDateRange.temporalType, setBenchmarkingDateRange]
  );

  // Handle date range change with automatic comparison recalculation
  const handleChange = useCallback(
    (newValue: DateValue) => {
      const comparisonPresetIds = isWeekly
        ? BENCHMARKING_WEEKLY_COMPARISON_PRESET_IDS
        : BENCHMARKING_MONTHLY_COMPARISON_PRESET_IDS;

      if (comparisonPresetIds.length > 0) {
        const newComparison = isWeekly
          ? recalcComparison(benchmarkingDateRange, newValue, WEEKLY_COMPARISON_REGISTRY, 'previous_period')
          : recalcComparison(benchmarkingDateRange, newValue, MONTHLY_COMPARISON_REGISTRY, 'previous_period');
        const validComparison = isValidComparisonRange(newComparison ?? {})
          ? newComparison
          : { presetValue: 'no_comparison' };
        setBenchmarkingDateRange({
          ...benchmarkingDateRange,
          range: newValue.range,
          comparison: validComparison,
        });
      } else {
        setBenchmarkingDateRange({
          ...benchmarkingDateRange,
          range: newValue.range,
        });
      }
    },
    [isWeekly, benchmarkingDateRange, setBenchmarkingDateRange]
  );

  return (
    <DateRangePicker
      granularity={granularity}
      onGranularityChange={handleGranularityChange}
      allowedGranularities={GRANULARITY_OPTIONS}
      value={fieldValue}
      onChange={handleChange}
      presetOptions={presetOptions}
      caption={caption}
      disabled={disabled}
      minDate={mainMinDate}
      maxDate={maxDate}
      disabledDates={disabledDates}
      onValidate={(startDate, endDate) => isValidDateRange(startDate, endDate, isWeekly, intl)}
      hideButtons
    />
  );
}

/**
 * Comparison date range picker for Benchmarking module.
 * Connected to the app store for global benchmarking date range state.
 * Includes integrated granularity toggle (weekly/monthly).
 * Edits the comparison portion of the date range.
 */
// eslint-disable-next-line react-refresh/only-export-components
function Comparison({ caption, disabled = false }: BenchmarkingDateRangePickerProps) {
  const intl = useIntl();
  const today = useMemo(() => new Date(), []);
  const { benchmarkingDateRange, setBenchmarkingDateRange } = useAppStore();

  const isWeekly = benchmarkingDateRange.temporalType === BENCHMARKING_TEMPORAL_TYPE_WEEKLY;
  const granularity = toGranularity(benchmarkingDateRange.temporalType);
  const { comparisonMinDate, comparisonMaxDate } = useDateBounds(isWeekly, benchmarkingDateRange.range);

  // Compute fixed number of months for comparison mode (monthly only)
  const fixedNumberOfMonth = useMemo(() => {
    if (!isWeekly && benchmarkingDateRange.range.to && benchmarkingDateRange.range.from) {
      return differenceInMonths(benchmarkingDateRange.range.to, benchmarkingDateRange.range.from) + 1;
    }
    return undefined;
  }, [isWeekly, benchmarkingDateRange.range.from, benchmarkingDateRange.range.to]);

  // Compute mainRangeDuration for weekly comparison mode (in days)
  const mainRangeDuration = useMemo(() => {
    if (isWeekly && benchmarkingDateRange.range.from && benchmarkingDateRange.range.to) {
      const weeks = differenceInWeeks(benchmarkingDateRange.range.to, benchmarkingDateRange.range.from) + 1;
      return weeks * 7 - 1; // Convert to days (subtract 1 because range is inclusive)
    }
    return undefined;
  }, [isWeekly, benchmarkingDateRange.range.from, benchmarkingDateRange.range.to]);

  // Build comparison preset options based on current granularity
  const presetOptions = useMemo<PresetOption[]>(() => {
    if (isWeekly) {
      return buildComparisonOptions(
        BENCHMARKING_WEEKLY_COMPARISON_PRESET_IDS,
        WEEKLY_COMPARISON_REGISTRY,
        today,
        benchmarkingDateRange.range
      );
    }
    return buildComparisonOptions(
      BENCHMARKING_MONTHLY_COMPARISON_PRESET_IDS,
      MONTHLY_COMPARISON_REGISTRY,
      today,
      benchmarkingDateRange.range
    );
  }, [isWeekly, today, benchmarkingDateRange.range]);

  // Compute the value to pass to the picker (comparison as "range")
  const fieldValue: DateValue = useMemo(
    () => ({
      range: benchmarkingDateRange.comparison ?? { presetValue: 'no_comparison' },
      comparison: undefined,
    }),
    [benchmarkingDateRange.comparison]
  );

  // Disable dates outside the allowed range
  const disabledDates = useCallback(
    (date: Date) => {
      if (comparisonMinDate && date < comparisonMinDate) return true;
      if (comparisonMaxDate && date > comparisonMaxDate) return true;
      return false;
    },
    [comparisonMinDate, comparisonMaxDate]
  );

  // Handle granularity change
  const handleGranularityChange = useCallback(
    (newGranularity: CalendarGranularity) => {
      const newTemporalType = toTemporalType(newGranularity);
      if (newTemporalType !== benchmarkingDateRange.temporalType) {
        // Reset to default range for the new granularity
        const newToday = new Date();
        const newIsWeekly = newGranularity === 'weekly';
        const newPresets = newIsWeekly
          ? buildPresetOptions(BENCHMARKING_WEEKLY_PRESET_IDS, WEEKLY_PRESET_REGISTRY, newToday)
          : buildPresetOptions(BENCHMARKING_MONTHLY_PRESET_IDS, MONTHLY_PRESET_REGISTRY, newToday);

        // Select the first non-custom preset as default
        const defaultPreset = newPresets.find(p => p.value !== 'custom') ?? newPresets[0];
        const newRange = {
          from: defaultPreset?.from,
          to: defaultPreset?.to,
          presetValue: defaultPreset?.value,
          presetLabel: defaultPreset?.label,
        };

        // Compute a default comparison preset for the new granularity
        const newComparison = newIsWeekly
          ? recalcComparison(
              { range: newRange, comparison: { presetValue: 'previous_period' } },
              { range: newRange },
              WEEKLY_COMPARISON_REGISTRY,
              'previous_period'
            )
          : recalcComparison(
              { range: newRange, comparison: { presetValue: 'previous_period' } },
              { range: newRange },
              MONTHLY_COMPARISON_REGISTRY,
              'previous_period'
            );
        const validComparison = isValidComparisonRange(newComparison ?? {})
          ? newComparison
          : { presetValue: 'no_comparison' };

        setBenchmarkingDateRange({
          temporalType: newTemporalType,
          range: newRange,
          comparison: validComparison,
        });
      }
    },
    [benchmarkingDateRange.temporalType, setBenchmarkingDateRange]
  );

  // Handle comparison date range change
  const handleChange = useCallback(
    (newValue: DateValue) => {
      const range = newValue.range;
      const validRange = isValidComparisonRange(range) ? range : { presetValue: 'no_comparison' };
      setBenchmarkingDateRange({
        ...benchmarkingDateRange,
        comparison: validRange,
      });
    },
    [benchmarkingDateRange, setBenchmarkingDateRange]
  );

  return (
    <DateRangePicker
      granularity={granularity}
      onGranularityChange={handleGranularityChange}
      allowedGranularities={GRANULARITY_OPTIONS}
      value={fieldValue}
      onChange={handleChange}
      presetOptions={presetOptions}
      caption={caption}
      disabled={disabled}
      minDate={comparisonMinDate}
      maxDate={comparisonMaxDate}
      disabledDates={disabledDates}
      fixedNumberOfMonth={fixedNumberOfMonth}
      onValidate={(startDate, endDate) => isValidDateRange(startDate, endDate, isWeekly, intl)}
      isComparison
      mainRangeDuration={mainRangeDuration}
      hideButtons
    />
  );
}

/**
 * Compound component for Benchmarking date range pickers.
 *
 * @example
 * <BenchmarkingDateRangePicker.Main caption="filter.key.selectedDateRange" />
 * <BenchmarkingDateRangePicker.Comparison caption="filter.key.selectedDateRangeComparison" />
 */
export const BenchmarkingDateRangePicker = {
  Main,
  Comparison,
};
